<template>
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
            <div class="flex items-center justify-between mb-4">
                <h3
                    class="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                >
                    {{ t("dashboard.onlineUsers") }}
                </h3>
                <div class="flex items-center space-x-2">
                    <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                    >
                        <span
                            class="w-2 h-2 mr-1.5 bg-green-500 rounded-full animate-pulse"
                        ></span>
                        {{ totalOnline }} {{ t("dashboard.online") }}
                    </span>
                    <button
                        @click="refreshOnlineUsers"
                        :disabled="isLoading"
                        class="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        :title="t('common.refresh')"
                    >
                        <RefreshCw
                            :class="[
                                'h-5 w-5 text-gray-500 dark:text-gray-400',
                                isLoading ? 'animate-spin' : '',
                            ]"
                        />
                    </button>
                </div>
            </div>

            <!-- Online by Role Summary -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <div
                        class="text-xs text-blue-600 dark:text-blue-400 font-medium"
                    >
                        {{ t("roles.student") }}
                    </div>
                    <div
                        class="text-lg font-semibold text-blue-900 dark:text-blue-100"
                    >
                        {{ onlineByRole.student }}
                    </div>
                </div>
                <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <div
                        class="text-xs text-green-600 dark:text-green-400 font-medium"
                    >
                        {{ t("roles.teacher") }}
                    </div>
                    <div
                        class="text-lg font-semibold text-green-900 dark:text-green-100"
                    >
                        {{ onlineByRole.teacher }}
                    </div>
                </div>
                <div class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                    <div
                        class="text-xs text-purple-600 dark:text-purple-400 font-medium"
                    >
                        {{ t("roles.administrator") }}
                    </div>
                    <div
                        class="text-lg font-semibold text-purple-900 dark:text-purple-100"
                    >
                        {{ onlineByRole.administrator }}
                    </div>
                </div>
                <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                    <div
                        class="text-xs text-yellow-600 dark:text-yellow-400 font-medium"
                    >
                        {{ t("roles.superAdministrator") }}
                    </div>
                    <div
                        class="text-lg font-semibold text-yellow-900 dark:text-yellow-100"
                    >
                        {{ onlineByRole["super-administrator"] }}
                    </div>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="flex justify-center items-center h-32">
                <Loader2 class="animate-spin h-8 w-8 text-primary-600" />
            </div>

            <!-- Empty State -->
            <div
                v-else-if="users.length === 0"
                class="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400"
            >
                <Users class="h-12 w-12 mb-2 opacity-50" />
                <p class="text-sm">{{ t("dashboard.noOnlineUsers") }}</p>
            </div>

            <!-- Online Users List -->
            <div v-else class="space-y-2 max-h-64 overflow-y-auto">
                <div
                    v-for="user in users"
                    :key="user.id"
                    class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <div class="flex items-center space-x-3">
                        <div class="relative">
                            <div
                                class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium"
                            >
                                {{ getInitials(user.name) }}
                            </div>
                            <span
                                v-if="user.online"
                                class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"
                            ></span>
                        </div>
                        <div>
                            <div
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ user.name }}
                            </div>
                            <div
                                class="text-xs text-gray-500 dark:text-gray-400"
                            >
                                {{ user.email }}
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span
                            :class="[
                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                getRoleBadgeClass(user.role),
                            ]"
                        >
                            {{ t(`roles.${formatRole(user.role)}`) }}
                        </span>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ formatLastActivity(user.last_activity_at, now) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RefreshCw, Users, Loader2 } from "@lucide/vue";
import { useOnlineStore } from "@/stores/onlineStore";

const { t } = useI18n();
const onlineStore = useOnlineStore();

const now = ref(Date.now());
let tickTimer: ReturnType<typeof setInterval>;

onMounted(() => {
    tickTimer = setInterval(() => {
        now.value = Date.now();
    }, 10000);
});

onUnmounted(() => {
    clearInterval(tickTimer);
});

interface OnlineUser {
    id: string;
    name: string;
    email: string;
    role: string;
    last_activity_at?: number | null;
    online?: boolean;
}

// State - read from store
const isLoading = computed(() => false);
const users = computed(() => onlineStore.users);
const totalOnline = computed(() => onlineStore.totalOnline());
const onlineByRole = computed(() => onlineStore.onlineByRole());

const refreshOnlineUsers = () => {
    // Presence channel handles real-time updates
};

const getInitials = (name: string): string => {
    return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};

const formatRole = (role: string): string => {
    const roleMap: Record<string, string> = {
        "super-administrator": "superAdministrator",
        administrator: "administrator",
        teacher: "teacher",
        student: "student",
    };
    return roleMap[role] || role;
};

const getRoleBadgeClass = (role: string): string => {
    const classMap: Record<string, string> = {
        "super-administrator":
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        administrator:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
        teacher:
            "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        student:
            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    };
    return (
        classMap[role] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const formatLastActivity = (dateString?: number | null): string => {
    if (dateString == null) {
        return t("time.justNow");
    }

    const timestamp =
        typeof dateString === "number"
            ? dateString * 1000
            : Date.parse(dateString);

    if (isNaN(timestamp)) {
        return t("time.justNow");
    }

    const now = Date.now();
    const diffInSeconds = Math.floor((now - timestamp) / 1000);

    if (diffInSeconds < 60) {
        return t("time.justNow");
    } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return t("time.minutesAgo", { count: minutes });
    } else {
        const hours = Math.floor(diffInSeconds / 3600);
        return t("time.hoursAgo", { count: hours });
    }
};
</script>
